import { ChangeEventHandler, useRef } from 'react';
import styled from 'styled-components';
import { InfoIcon, Icon, P, SvgInlineWrapper, Div, TooltipWrapper, lightTheme } from '@vgrubs/components';
import { UploadedImage } from '../UploadedImage/UploadedImage';
import { ErrorToast } from '../Toasts/Toasts';
import Compressor from 'compressorjs';

const AdminInputStyled = styled.div<{
  $textCenter?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $textCenter }) => ($textCenter ? 'center' : 'flex-start')};
  input {
    visibility: hidden;
    height: 0;
    width: 0;
  }
`;

export const AdminImageInput = ({
  image,
  onChange,
  label,
  placeholder,
  onDelete,
  noChange,
  textCenter,
  shape = 'square',
  width = '100%',
  height,
  heightSM,
  tooltip
}: {
  image: File | Blob | string | null;
  onChange: (img: File | Blob | string | null) => void;
  label?: string;
  placeholder?: React.ElementType | string;
  onDelete?: () => void;
  noChange?: boolean;
  textCenter?: boolean;
  shape?: 'square' | 'circle';
  width?: string;
  height?: string;
  heightSM?: string;
  tooltip?: string;
}) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const onBtnClick = () => {
    if (noChange) return;
    inputFileRef.current?.click();
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = e.target.files;
    const file = files && files[0];
    const maxSize = 2097152;

    if (file) {
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 2500,
        maxHeight: 1500,
        convertSize: 2097152,
        success: (compressedResult) => {
          if (compressedResult.size > maxSize) {
            return ErrorToast('Image file too big');
          } else {
            onChange(compressedResult);
          }
        },
      });
    }
  };

  return (
    <AdminInputStyled $textCenter={textCenter}>
      <Div $flex $alignCenter $gap="4px" $mb="10">
        <P $mb="0">
          <b>{label}</b>
        </P>
        {tooltip && <TooltipWrapper tooltip={tooltip} >
          <SvgInlineWrapper>
            <Icon color={lightTheme.darkGray}>
              <InfoIcon />
            </Icon>
          </SvgInlineWrapper>
        </TooltipWrapper>}
      </Div>
      <UploadedImage
        shape={shape}
        image={image}
        PlaceholderImage={placeholder as React.ElementType}
        onDelete={onDelete}
        onClick={onBtnClick}
        noChange={noChange}
        width={width}
        height={height}
        heightSM={heightSM}
      />

      <input
        ref={inputFileRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
      />
    </AdminInputStyled>
  );
};
